/**
 * OPP Shared Constants
 *
 * Centralized configuration for badges, status, levels, and types
 * to ensure consistency across admin pages.
 */

// ---- OP Code Format ----
export const OP_CODE_REGEX = /^[A-Z0-9]{6}$/;
export const OP_CODE_LENGTH = 6;

// ---- Time Constants ----
export const ONE_DAY_MS = 86400000; // 24 * 60 * 60 * 1000
export const AUTO_SAVE_INTERVAL_MS = 30000; // 30 seconds
export const DRAFT_SAVED_TOAST_DURATION_MS = 2000; // 2 seconds
export const SEARCH_DEBOUNCE_DELAY_MS = 300; // 300ms for search input debounce

// ---- Pagination Constants ----
export const DEFAULT_LETTERS_LIMIT = 50;
export const DEFAULT_COURIER_TASKS_LIMIT = 20;
export const DEFAULT_TEMPLATES_LIMIT = 20;
export const DEFAULT_ENVELOPE_DESIGNS_LIMIT = 20;
export const DEFAULT_PUBLIC_LETTERS_LIMIT = 10;
export const DEFAULT_MUSEUM_PAGE_SIZE = 12;
export const DEFAULT_EXHIBITIONS_LIMIT = 10;
export const DEFAULT_NOTIFICATIONS_LIMIT = 10;

// ---- File Upload Constants ----
export const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;
export const IMAGE_EXTENSION_MAP: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

// ---- Letter Limits ----
export const MAX_LETTER_CONTENT_LENGTH = 50000;
export const MAX_LETTER_TITLE_LENGTH = 100;
export const MIN_LETTER_CONTENT_LENGTH = 1;

// ---- Signed URL Cache ----
export const SIGNED_URL_STALE_TIME_MS = 60 * 60 * 1000; // 1 hour
export const SIGNED_URL_GC_TIME_MS = 60 * 60 * 1000; // 1 hour

// ---- Notification Polling ----
export const NOTIFICATION_REFETCH_INTERVAL_MS = 30000; // 30 seconds

// ---- Courier Level Configuration ----
export const OPP_LEVEL_CONFIG: Record<number, { label: string; bgClass: string; colorClass: string }> = {
  1: { label: 'L1 楼栋投递员', bgClass: 'bg-[hsl(var(--opp-accent-soft))]', colorClass: 'text-[hsl(var(--opp-accent-strong))]' },
  2: { label: 'L2 片区管理员', bgClass: 'bg-blue-100', colorClass: 'text-blue-700' },
  3: { label: 'L3 学校协调员', bgClass: 'bg-[hsl(var(--opp-accent-soft))]', colorClass: 'text-[hsl(var(--opp-accent-strong))]' },
  4: { label: 'L4 城市总监', bgClass: 'bg-gray-100', colorClass: 'text-gray-700' },
};

// ---- Courier Status Configuration ----
export const OPP_STATUS_CONFIG: Record<string, { label: string; bgClass: string; colorClass: string }> = {
  approved: { label: '已通过', bgClass: 'bg-green-100', colorClass: 'text-green-700' },
  suspended: { label: '暂停', bgClass: 'bg-gray-100', colorClass: 'text-gray-600' },
  pending: { label: '待审核', bgClass: 'bg-[hsl(var(--opp-accent-soft))]', colorClass: 'text-[hsl(var(--opp-accent-strong))]' },
  rejected: { label: '已拒绝', bgClass: 'bg-[hsl(var(--opp-danger-bg))]', colorClass: 'text-[hsl(var(--opp-danger-fg))]' },
};

// ---- Envelope Design Status Badge ----
export const OPP_ENVELOPE_STATUS_BADGE: Record<string, { bgClass: string; colorClass: string; label: string }> = {
  pending: { bgClass: 'bg-[hsl(var(--opp-accent-soft))]', colorClass: 'text-[hsl(var(--opp-accent-strong))]', label: '待审核' },
  approved: { bgClass: 'bg-green-100', colorClass: 'text-green-700', label: '已通过' },
  rejected: { bgClass: 'bg-[hsl(var(--opp-danger-bg))]', colorClass: 'text-[hsl(var(--opp-danger-fg))]', label: '已拒绝' },
};

// ---- Envelope Design Type Badge ----
export const OPP_ENVELOPE_TYPE_BADGE: Record<string, { bgClass: string; colorClass: string; label: string }> = {
  city: { bgClass: 'bg-blue-100', colorClass: 'text-blue-700', label: '城市' },
  campus: { bgClass: 'bg-[hsl(var(--opp-accent-soft))]', colorClass: 'text-[hsl(var(--opp-accent-strong))]', label: '校园' },
};
